import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router";
import { supabase } from "@/lib/supabase";
import { useAuthStore } from "@/stores/auth";
import { getAppMetadata } from "@/lib/jwt";
import { useTranslation } from "@/i18n/use-translation";

export function ProtectedRoute() {
  const { t } = useTranslation();
  const session = useAuthStore((s) => s.session);
  const user = useAuthStore((s) => s.user);
  const isLoading = useAuthStore((s) => s.isLoading);
  const location = useLocation();
  const [aalLevel, setAalLevel] = useState<string | null>(null);
  const [aalChecked, setAalChecked] = useState(false);

  useEffect(() => {
    if (!user) return;
    supabase.auth.mfa.getAuthenticatorAssuranceLevel().then(({ data }) => {
      setAalLevel(data?.currentLevel ?? null);
      setAalChecked(true);
    });
  }, [user, session]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
      </div>
    );
  }

  if (!user) {
    const returnTo = location.pathname + location.search;
    return <Navigate to={`/login?redirect=${encodeURIComponent(returnTo)}`} replace />;
  }

  // Wait for AAL check to complete before deciding
  if (!aalChecked) {
    return (
      <div className="flex h-screen items-center justify-center">
        <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
      </div>
    );
  }

  // Check if MFA is enrolled but not yet verified for this session
  const phoneFactor = user.factors?.find(
    (f) => f.factor_type === "phone" && f.status === "verified",
  );

  if (phoneFactor && aalLevel !== "aal2") {
    return <Navigate to="/mfa-challenge" replace />;
  }

  // Check if user has an org assignment or is a platform admin via JWT custom claims
  const claims = session?.access_token ? getAppMetadata(session.access_token) : null;

  // A labeller (POS2-1057) is platform-level and belongs to no org, so neither
  // of the first two claims is set for them — without this they would be sent
  // to /waiting before any labelling route could render.
  const hasAccess = claims?.org_id || claims?.is_platform_admin || claims?.is_labeller;

  if (!hasAccess) {
    return <Navigate to="/waiting" replace />;
  }

  return <Outlet />;
}
